import { Text } from '@fluentui/react';
import { ReactElement } from 'react';
import { effectivenessKeys } from '@groove/api/v2/emailEffectiveness';
import pluralize from 'pluralize';

type CreateDisplayComponents = {
  [key in effectivenessKeys]: {
    upperValue: number;
    displayPercentage: number;
    displayComponent: (length: number) => ReactElement | null;
  }[];
};

export const createDisplayComponents: CreateDisplayComponents = {
  emailLength: [
    {
      upperValue: 0,
      displayPercentage: 0,
      displayComponent: () => <Text>Please start writing your email.</Text>,
    },
    {
      upperValue: 75,
      displayPercentage: 90,
      displayComponent: length => (
        <Text>
          Your email contains{' '}
          <b>
            {length} {pluralize('word', length)}
          </b>
          . The ideal email length is between 51 and 125 words.
        </Text>
      ),
    },
    {
      upperValue: 76,
      displayPercentage: 100,
      displayComponent: () => <Text>Your email is the ideal length</Text>,
    },
    {
      upperValue: 120,
      displayPercentage: 100,
      displayComponent: () => <Text>Your email is the ideal length</Text>,
    },
    {
      upperValue: 500,
      displayPercentage: 20,
      displayComponent: length => (
        <Text>
          Your email is <b>too long</b>. Please consider shortening it to less
          than 125 words. It contains{' '}
          <b>
            {length} {pluralize('word', length)}
          </b>
          .
        </Text>
      ),
    },
    {
      upperValue: 10000,
      displayPercentage: 0,
      displayComponent: length => (
        <Text>
          Your email is <b>extremely long</b>. Please consider shortening it to
          less than 125 words. It contains{' '}
          <b>
            {length} {pluralize('word', length)}
          </b>
          .
        </Text>
      ),
    },
  ],
  subjectLength: [
    {
      upperValue: 0,
      displayPercentage: 0,
      displayComponent: () => <Text>Please add an email subject.</Text>,
    },
    {
      upperValue: 2,
      displayPercentage: 80,
      displayComponent: length => (
        <Text>
          The ideal subject line length is 3-4 words. Yours is currently{' '}
          <b>
            {length} {pluralize('word', length)}
          </b>
          .
        </Text>
      ),
    },
    {
      upperValue: 3,
      displayPercentage: 100,
      displayComponent: () => (
        <Text>Your email subject line is the ideal length.</Text>
      ),
    },
    {
      upperValue: 4,
      displayPercentage: 100,
      displayComponent: () => (
        <Text>Your email subject line is the ideal length.</Text>
      ),
    },
    {
      upperValue: 20,
      displayPercentage: 20,
      displayComponent: length => (
        <Text>
          The ideal subject line length is 3-4 words. Yours is currently{' '}
          <b>
            {length} {pluralize('word', length)}
          </b>
          .
        </Text>
      ),
    },
  ],
  positivity: [
    {
      upperValue: 0,
      displayPercentage: 0,
      displayComponent: () => null,
    },
    {
      upperValue: 0.2,
      displayPercentage: 30,
      displayComponent: () => (
        <Text>
          Your email is very negative. Consider rewording your email to be more
          positive.
        </Text>
      ),
    },
    {
      upperValue: 0.4,
      displayPercentage: 60,
      displayComponent: () => (
        <Text>
          Your email is somewhat negative. Consider rewording your email to be
          more positive.
        </Text>
      ),
    },
    {
      upperValue: 0.6,
      displayPercentage: 90,
      displayComponent: () => (
        <Text>Your email is written in a neutral tone.</Text>
      ),
    },
    {
      upperValue: 0.6001,
      displayPercentage: 85,
      displayComponent: () => (
        <Text>Your email is written in a positive tone.</Text>
      ),
    },
    {
      upperValue: 0.85,
      displayPercentage: 85,
      displayComponent: () => (
        <Text>Your email is written in a positive tone.</Text>
      ),
    },
    {
      upperValue: 0.85001,
      displayPercentage: 100,
      displayComponent: () => (
        <Text>Your email is written in a positive tone.</Text>
      ),
    },
    {
      upperValue: 1,
      displayPercentage: 100,
      displayComponent: () => (
        <Text>Your email is written in a positive tone.</Text>
      ),
    },
  ],
  summary: [
    {
      upperValue: 0,
      displayPercentage: 0,
      displayComponent: () => <Text>Please start drafting your email.</Text>,
    },
    {
      upperValue: 33,
      displayPercentage: 33,
      displayComponent: () => <Text>Please see recommendations below.</Text>,
    },
    {
      upperValue: 66,
      displayPercentage: 66,
      displayComponent: () => <Text>Please see recommendations below.</Text>,
    },
    {
      upperValue: 90,
      displayPercentage: 90,
      displayComponent: () => <Text>Please see recommendations below.</Text>,
    },
    {
      upperValue: 100,
      displayPercentage: 100,
      displayComponent: () => (
        <Text>Your email is the ideal level of readability.</Text>
      ),
    },
  ],
  gradeLevel: [
    {
      upperValue: 0,
      displayPercentage: 0,
      displayComponent: () => null,
    },
    {
      upperValue: 4,
      displayPercentage: 80,
      displayComponent: grade => (
        <Text>
          You email is written at a <b>{grade} grade</b> reading level. The
          ideal reading grade level is between 5 - 8 grade.
        </Text>
      ),
    },
    {
      upperValue: 5,
      displayPercentage: 100,
      displayComponent: grade => (
        <Text>Your email is written at an ideal reading grade level.</Text>
      ),
    },
    {
      upperValue: 8,
      displayPercentage: 100,
      displayComponent: grade => (
        <Text>Your email is written at an ideal reading grade level.</Text>
      ),
    },
    {
      upperValue: 9,
      displayPercentage: 80,
      displayComponent: grade => (
        <Text>
          You email is written at a <b>{grade} grade</b> reading level. The
          ideal reading grade level is between 5 - 8 grade. Use fewer complex
          words.
        </Text>
      ),
    },
    {
      upperValue: 20,
      displayPercentage: 20,
      displayComponent: grade => (
        <Text>
          You email is written at a <b>{grade} grade</b> reading level. The
          ideal reading grade level is between 5 - 8 grade. Use fewer complex
          words.
        </Text>
      ),
    },
  ],
};
